import { Dialog, DialogActions, DialogContent, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { PublicationActionType, ReportTypesSelectorResponse } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    commentId?: string;
    title: string;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const ReportErrorOrAbuseDialog: React.FC<IProps> = (props) => {
    const { publicationId, commentId, title, onClose } = props;

    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();

    const [text, setText] = useState<string | null>(null);
    const [reportType, setReportType] = useState<PublicationActionType | undefined>();
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewReportTypes, setViewReportTypes] = useState<ReportTypesSelectorResponse[]>([]);

    const canSave = (reportType != undefined && reportType != PublicationActionType.ReportOther) || (reportType == PublicationActionType.ReportOther && text != undefined && text.length > 0);


    ///
    const handleSave = () => {
        if (!canSave) return;

        setSaving(true);
        api.v1.knowledgePublication.addAbuseReport({
            publicationId,
            actionType: reportType!,
            message: text,
            commentId: commentId == undefined ? null : commentId
        }).then(result => {
            if (result.data === true) {
                enqueueSnackbar(t("Feed.Card.Actions.ReportErrorOrAbuseDialog.SaveSuccessMessage"), { variant: "success" });
                onClose();
            }
            else {
                enqueueSnackbar(t("Feed.Card.Actions.ReportErrorOrAbuseDialog.SaveFailMessage"), { variant: "error" });
            }
        }).catch(e => {
            const re = MprcResolveRequestErrors(e);
            if (re.fieldsWithError != undefined && (
                re.fieldsWithError["publicationId"] ||
                re.fieldsWithError["actionType"] ||
                re.fieldsWithError["message"])) {

                let errors = (re.fieldsWithError["publicationId"] ?? []).concat(re.fieldsWithError["actionType"] ?? []).concat(re.fieldsWithError["message"] ?? []);
                errors.forEach(item => enqueueSnackbar(item, { variant: "error" }));
            } else {
                setViewError(e);
            }
        })
            .finally(() => setSaving(false));
    };

    ///
    const handleChangeReportType = (e: SelectChangeEvent<PublicationActionType>) => {
        setReportType(e.target.value as PublicationActionType);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.knowledgeSelectors.reportTypes();
                setViewReportTypes(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    useEffect(() => {
        if (viewReportTypes && viewReportTypes.length > 0) {
            setReportType(viewReportTypes[0].type);
        }
    }, [viewReportTypes]);

    return (<Dialog
        open
        onClose={onClose}
        maxWidth="xs"
        fullWidth
    >
        <MprcDialogTitleClose
            title={t("Feed.Card.Actions.ReportErrorOrAbuseDialog.Title")}
            subtitle={title}
            onClose={onClose}
            shrinkPadding
        />
        <DialogContent>
            <MprcRequestIndicator type={loading ? "request" : "post"} visible={saving || loading} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <MprcContainer>
                <FormControl fullWidth>
                    <InputLabel>{t("Feed.Card.Actions.ReportErrorOrAbuseDialog.ReportTypeLabel")}</InputLabel>
                    <Select
                        label={t("Feed.Card.Actions.ReportErrorOrAbuseDialog.ReportTypeLabel")}
                        value={reportType ?? ""}
                        onChange={handleChangeReportType}
                        autoFocus
                    >
                        {viewReportTypes.map(item =>
                            <MenuItem key={item.type} value={item.type}>{item.name}</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </MprcContainer>

            <TextField
                value={text}
                placeholder={t("Feed.Card.Actions.ReportErrorOrAbuseDialog.MessagePlaceholder")}
                multiline={true}
                rows={10}
                onChange={(e) => setText(e.target.value)}
                fullWidth
                variant="outlined"
                error={text != undefined && !canSave}
            />
            <Typography variant="caption">{t("Feed.Card.Actions.ReportErrorOrAbuseDialog.MessageHint")}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
            <MprcButton
                text={t("Feed.Card.Actions.ReportErrorOrAbuseDialog.SaveButtonText")}
                variant="contained"
                color="warning"
                onClick={handleSave}
                disabled={!canSave}
            />
        </DialogActions>
    </Dialog>
    );
};
